import React, {SyntheticEvent, DragEvent, useCallback, FC, useMemo, useRef} from 'react';
import {makeStyles} from '@material-ui/core';
import {heightUploadResources} from 'utils';
import {UploadSingleFile} from 'admin/components';
import {useFormikContext} from 'formik';
import {IWithDisabled} from 'models/common';
import {DraggableArea, Row} from 'components-lib';
import {useFileSelectionLoading} from 'hooks';

interface ISingleFileUploadProps extends IWithDisabled {
  name: string;
  accept?: string;
  title?: string;
  position?: 'horizontal' | 'vertical';
  withOnlyBrowseButton?: boolean;
  disableDragAndDrop?: boolean;
  fullHeight?: boolean;
  fullWidth?: boolean;
  align?: 'flex-start' | 'center' | 'flex-end';
  justify?: 'flex-start' | 'center' | 'flex-end';
  deleteCallback?: () => void;
}

export const SingleFileUpload: FC<ISingleFileUploadProps> = ({
  name,
  accept = '*',
  position = 'vertical',
  disabled = false,
  disableDragAndDrop = false,
  withOnlyBrowseButton = false,
  fullHeight = true,
  fullWidth = true,
  align = 'center',
  justify = 'center',
  title = 'Upload File',
  deleteCallback,
}) => {
  const classes = useStyles({fullHeight, fullWidth});
  const {setFieldValue} = useFormikContext<any>();
  const fileSelectionLoading = useFileSelectionLoading();
  const dragDepthRef = useRef(0);
  const isCommittingDropRef = useRef(false);

  const handleFileDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.stopPropagation();
      event.preventDefault();
      isCommittingDropRef.current = true;
      dragDepthRef.current = 0;

      const newFile = event.dataTransfer.files[0];
      if (!newFile) {
        isCommittingDropRef.current = false;
        fileSelectionLoading?.clearLoading();
        return;
      }

      if (fileSelectionLoading) {
        fileSelectionLoading.applySelectedFile(newFile);
      } else {
        setFieldValue(name, newFile);
      }

      isCommittingDropRef.current = false;
    },
    [fileSelectionLoading, name, setFieldValue]
  );

  const handleDragOver = useCallback((event: SyntheticEvent) => {
    event.stopPropagation();
    event.preventDefault();
  }, []);

  const handleDragEnter = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.stopPropagation();
      event.preventDefault();
      dragDepthRef.current += 1;

      if (dragDepthRef.current === 1 && event.dataTransfer?.types?.includes('Files')) {
        fileSelectionLoading?.startLoading();
      }
    },
    [fileSelectionLoading]
  );

  const handleDragLeave = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.stopPropagation();
      event.preventDefault();
      dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);

      if (dragDepthRef.current === 0 && !isCommittingDropRef.current) {
        fileSelectionLoading?.clearLoading();
      }
    },
    [fileSelectionLoading]
  );

  const singleFileUpload = useMemo(
    () => (
      <UploadSingleFile
        disabled={disabled}
        title={title}
        position={position}
        accept={accept}
        name="file"
        loading={false}
        withOnlyBrowseButton={withOnlyBrowseButton}
        deleteCallback={deleteCallback}
      />
    ),
    [accept, disabled, position, title, withOnlyBrowseButton, deleteCallback]
  );

  return (
    <Row align={align} justify={justify} classes={{root: classes.content}}>
      {disableDragAndDrop && singleFileUpload}
      {!disableDragAndDrop && (
        <div className={classes.dragHost} onDragEnter={handleDragEnter} onDragLeave={handleDragLeave}>
          <DraggableArea onDrop={handleFileDrop} onDragOver={handleDragOver} disabled={disabled}>
            {singleFileUpload}
          </DraggableArea>
        </div>
      )}
    </Row>
  );
};

const useStyles = makeStyles(() => ({
  content: ({fullHeight, fullWidth}: Pick<ISingleFileUploadProps, 'fullHeight' | 'fullWidth'>) => {
    return {
      width: fullWidth ? '100%' : '',
      height: fullHeight ? heightUploadResources : '',
    };
  },
  dragHost: {
    width: '100%',
    height: '100%',
  },
}));
